import React from "react";

/**
 * Reusable accessible field wrappers using react-hook-form `register`.
 * Consistent label + error styling across all 3 forms.
 */
function baseInputClasses(invalid, light) {
  const ring = invalid
    ? "border-red-500 focus:border-red-500 focus:ring-red-500/30"
    : light
    ? "border-white/30 focus:border-white focus:ring-white/30 bg-white/5 text-white placeholder-white/55"
    : "border-ridge-border focus:border-ridge-charcoal focus:ring-ridge-charcoal/15 bg-white text-ridge-text placeholder-ridge-muted";
  return [
    "block w-full rounded-md border px-3.5 py-3 text-sm md:text-base",
    "transition-colors duration-150",
    "focus:outline-none focus:ring-2",
    "disabled:cursor-not-allowed disabled:opacity-60",
    ring,
  ].join(" ");
}

export function FieldShell({
  label,
  required,
  error,
  hint,
  htmlFor,
  light,
  testId,
  children,
}) {
  return (
    <div className="w-full">
      <label
        htmlFor={htmlFor}
        className={`mb-1.5 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em] ${
          light ? "text-white/85" : "text-ridge-charcoal"
        }`}
      >
        {label}
        {required ? (
          <span aria-hidden="true" className="text-ridge-amber">
            *
          </span>
        ) : null}
      </label>
      {children}
      {hint && !error ? (
        <p className={`mt-1 text-xs ${light ? "text-white/60" : "text-ridge-muted"}`}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p
          className="mt-1 text-xs font-medium text-red-500"
          role="alert"
          data-testid={testId ? `${testId}-error` : undefined}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function TextField({
  name,
  label,
  type = "text",
  required,
  placeholder,
  hint,
  register,
  error,
  light,
  testId,
  autoComplete,
  ...rest
}) {
  return (
    <FieldShell
      label={label}
      required={required}
      error={error}
      hint={hint}
      light={light}
      htmlFor={name}
      testId={testId}
    >
      <input
        id={name}
        type={type}
        autoComplete={autoComplete}
        placeholder={placeholder}
        aria-invalid={!!error}
        data-testid={testId}
        className={baseInputClasses(!!error, light)}
        {...register(name)}
        {...rest}
      />
    </FieldShell>
  );
}

export function TextareaField({
  name,
  label,
  required,
  placeholder,
  hint,
  register,
  error,
  light,
  testId,
  rows = 4,
  maxLength,
  ...rest
}) {
  return (
    <FieldShell
      label={label}
      required={required}
      error={error}
      hint={hint}
      light={light}
      htmlFor={name}
      testId={testId}
    >
      <textarea
        id={name}
        rows={rows}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-invalid={!!error}
        data-testid={testId}
        className={baseInputClasses(!!error, light) + " resize-y"}
        {...register(name)}
        {...rest}
      />
    </FieldShell>
  );
}

export function SelectField({
  name,
  label,
  required,
  options,
  hint,
  register,
  error,
  light,
  testId,
  defaultValue = "",
  ...rest
}) {
  return (
    <FieldShell
      label={label}
      required={required}
      error={error}
      hint={hint}
      light={light}
      htmlFor={name}
      testId={testId}
    >
      <select
        id={name}
        aria-invalid={!!error}
        data-testid={testId}
        defaultValue={defaultValue}
        className={baseInputClasses(!!error, light) + " appearance-none pr-10 bg-no-repeat bg-[right_0.85rem_center]"}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%231C1C1C' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
        }}
        {...register(name)}
        {...rest}
      >
        <option value="" disabled>
          Select…
        </option>
        {options.map((opt) =>
          typeof opt === "string" ? (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ) : (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          )
        )}
      </select>
    </FieldShell>
  );
}
